/**
 * Report CRUD & subscriptions — Phase 5b replacement.
 * Delegates to /api/reports/* endpoints on dash-api instead of Firestore.
 * onSnapshot subscriptions are replaced with an initial fetch + STOMP live updates.
 */

import {setUserAccess} from '../realtime_database/access';
import {useStompSubscription} from '../../hooks/useStompSubscription';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) {
        const err = new Error(`${opts.method || 'GET'} ${path} → ${resp.status}`);
        err.status = resp.status;
        throw err;
    }
    if (resp.status === 204) return null;
    return resp.json();
}

/**
 * Short-TTL, request-coalescing cache for GET list fetches keyed by path.
 *
 * Several independently-mounted components subscribe to the same report list on
 * a single page load (e.g. DraftsPage and the global AI ContextBar both call
 * useUserDrafts -> /api/reports?userId=...&excludeStatus=approved). Without
 * sharing, each fired its own identical request. This collapses concurrent
 * identical GETs onto one in-flight promise and serves a fresh result from a
 * brief cache window, while always returning a defensive copy to callers.
 */
const LIST_TTL_MS = 30_000;
const listCache = new Map(); // path -> { at: number, data: any }
const listInFlight = new Map(); // path -> Promise<any>

function cachedListFetch(path, {force = false} = {}) {
    if (!force) {
        const hit = listCache.get(path);
        if (hit && (Date.now() - hit.at) < LIST_TTL_MS) {
            return Promise.resolve(hit.data);
        }
        const pending = listInFlight.get(path);
        if (pending) return pending;
    }

    const promise = apiFetch(path)
        .then((data) => {
            listCache.set(path, {at: Date.now(), data});
            return data;
        })
        .finally(() => {
            listInFlight.delete(path);
        });

    listInFlight.set(path, promise);
    return promise;
}

/** Invalidate cached report list GETs (call after any report mutation). */
export const invalidateReportListCache = () => {
    listCache.clear();
    listInFlight.clear();
};

export const getUserReports = async (userId) => {
    if (!userId) return [];
    const data = await cachedListFetch(`/api/reports?authorUid=${encodeURIComponent(userId)}`);
    return Array.isArray(data) ? [...data] : [];
};

export const getReportsByICRNumber = async (icrNumber) => {
    if (!icrNumber && icrNumber !== 0) return [];
    const data = await apiFetch(`/api/reports?icrNumber=${encodeURIComponent(icrNumber)}`);
    return Array.isArray(data) ? data : [];
};

/**
 * Subscribe to user reports. Returns an unsubscribe function.
 * Initial load via REST; live updates via STOMP /topic/reports.
 */
export const subscribeToUserReports = (userId, onChange, onError) => {
    if (!userId) {
        Promise.resolve().then(() => onChange?.([]));
        return () => {};
    }

    let cancelled = false;

    getUserReports(userId)
        .then((data) => { if (!cancelled) onChange?.(data); })
        .catch((err) => { if (!cancelled) onError?.(err); });

    // Note: STOMP live subscription is set up via useStompSubscription in components;
    // this callback-style subscription is kept for legacy callers.
    return () => { cancelled = true; };
};

function dedupeReportsById(rows) {
    const byId = new Map();
    for (const r of rows) {
        if (r?.id) byId.set(r.id, r);
    }
    return [...byId.values()];
}

/**
 * Composite fetch for the unified Products listing page.
 * Merges org-wide approved products, scoped non-approved (own + shared drafts), and
 * disclosure oversight (admin org-wide via productType filter on the backend).
 * Replaces a single unfiltered GET /api/reports which leaked unshared drafts for admins.
 */
export const subscribeToAllReports = (onChange, onError) => {
    let cancelled = false;
    Promise.all([
        cachedListFetch('/api/reports?status=approved'),
        cachedListFetch('/api/reports?excludeStatus=approved'),
        cachedListFetch('/api/reports?productType=disclosure'),
    ])
        .then(([approved, nonApproved, disclosures]) => {
            if (cancelled) return;
            const merged = dedupeReportsById([
                ...(Array.isArray(approved) ? approved : []),
                ...(Array.isArray(nonApproved) ? nonApproved : []),
                ...(Array.isArray(disclosures) ? disclosures : []),
            ]);
            onChange?.(merged);
        })
        .catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

export const subscribeToReportsForReview = (onChange, onError) => {
    let cancelled = false;
    apiFetch('/api/reports?status=Awaiting+Review')
        .then((data) => { if (!cancelled) onChange?.(Array.isArray(data) ? data : []); })
        .catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

/**
 * Fetches both "Awaiting Review" and "approved" reports for the reviewer queue.
 * "Awaiting Review" rows sort first; within each group, newest updatedAt first.
 */
export const subscribeToReviewerQueue = (onChange, onError) => {
    let cancelled = false;
    Promise.all([
        apiFetch('/api/reports?status=Awaiting+Review'),
        apiFetch('/api/reports?status=approved'),
    ]).then(([awaiting, approved]) => {
        if (cancelled) return;
        const pendingRows = Array.isArray(awaiting) ? awaiting : [];
        const approvedRows = Array.isArray(approved) ? approved : [];
        // Sort each group newest-first, then concatenate: pending before approved
        const byNewest = (a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '');
        onChange([...pendingRows.sort(byNewest), ...approvedRows.sort(byNewest)]);
    }).catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

export const subscribeToUserDrafts = (userId, onChange, onError) => {
    if (!userId) {
        Promise.resolve().then(() => onChange?.([]));
        return () => {};
    }
    let cancelled = false;
    cachedListFetch(`/api/reports?authorUid=${encodeURIComponent(userId)}&excludeStatus=approved`)
        .then((data) => { if (!cancelled) onChange?.(Array.isArray(data) ? [...data] : []); })
        .catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

export const getFintelsByAuthorUid = async (authorUid) => {
    if (!authorUid) return [];
    const data = await apiFetch(
        `/api/reports?status=approved&authorUid=${encodeURIComponent(authorUid)}`
    );
    return Array.isArray(data)
        ? data.filter(r => r.productType?.value !== 'disclosure')
        : [];
};

export const subscribeToUserFintel = (userId, onChange, onError) => {
    if (!userId) {
        Promise.resolve().then(() => onChange?.([]));
        return () => {};
    }
    let cancelled = false;
    cachedListFetch(`/api/reports?status=approved&authorUid=${encodeURIComponent(userId)}`)
        .then((data) => {
            if (!cancelled) onChange?.(
                Array.isArray(data) ? data.filter(r => r.productType?.value !== 'disclosure') : []
            );
        })
        .catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

export const subscribeToUserDisclosures = (userId, onChange, onError, {adminView = false} = {}) => {
    if (!userId) {
        Promise.resolve().then(() => onChange?.([]));
        return () => {};
    }
    let cancelled = false;
    // Admin users see all disclosures (any status); regular users see approved only.
    const query = adminView
        ? `/api/reports?productType=disclosure`
        : `/api/reports?status=approved&productType=disclosure`;
    cachedListFetch(query)
        .then((data) => { if (!cancelled) onChange?.(Array.isArray(data) ? [...data] : []); })
        .catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

export const getReportById = async (reportId) => {
    return apiFetch(`/api/reports/${encodeURIComponent(reportId)}`);
};

/**
 * Self-heal a report's search query. No-op server-side if the report already has a defaultQuery;
 * otherwise the backend derives a topic from the report title + latest content, generates a boolean
 * query, persists topic + defaultQuery, and returns it. Returns { defaultQuery, generated } or null.
 */
export const ensureReportSearchQuery = async (reportId) => {
    if (!reportId) return null;
    return apiFetch(`/api/reports/${encodeURIComponent(reportId)}/ensure-search-query`, {
        method: 'POST',
    });
};

export const getReportHeadersByIds = async (reportIds = []) => {
    const uniqueIds = [...new Set((reportIds || []).filter(Boolean))];
    if (!uniqueIds.length) return {};
    const data = await apiFetch(
        `/api/reports?ids=${uniqueIds.map(encodeURIComponent).join(',')}`
    );
    if (!Array.isArray(data)) return {};
    const byId = {};
    data.forEach((r) => {
        if (r.id) byId[r.id] = r;
    });
    return byId;
};

export const getDisclosuresByParentId = async (parentReportId) => {
    if (!parentReportId) return [];
    const data = await apiFetch(
        `/api/reports?parentId=${encodeURIComponent(parentReportId)}`
    );
    return Array.isArray(data) ? data : [];
};

export const getDisclosureCountsByParentIds = async (parentReportIds = []) => {
    const ids = [...new Set((parentReportIds || []).filter(Boolean))];
    if (!ids.length) return {};
    const data = await apiFetch(
        `/api/reports/disclosure-counts?parentIds=${ids.map(encodeURIComponent).join(',')}`
    );
    return data && typeof data === 'object' ? data : {};
};

export const createReport = async (reportData, docInitEntries = {}) => {
    const created = await apiFetch('/api/reports', {
        method: 'POST',
        body: JSON.stringify(reportData),
    });

    if (created?.id && reportData.userId) {
        try {
            await setUserAccess(created.id, reportData.userId, 'owner');
        } catch (err) {
            console.error('Failed to set access owner for report', {reportId: created.id, err});
        }
    }

    invalidateReportListCache();
    return {reportId: created?.id};
};

export const updateReportDoc = async (reportId, updateData) => {
    await apiFetch(`/api/reports/${encodeURIComponent(reportId)}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
    });
    invalidateReportListCache();
    return {id: reportId};
};

export const updateReportDocSilent = updateReportDoc;

export const updateReportMeta = async (reportId, meta) => {
    if (!reportId) throw new Error('reportId is required');
    await apiFetch(`/api/reports/${encodeURIComponent(reportId)}`, {
        method: 'PUT',
        body: JSON.stringify({meta}),
    });
    invalidateReportListCache();
    return {id: reportId};
};

export const ensureSectionContentDoc = async (reportId, sectionId) => {
    // Content is now owned by Yjs/ws-collaboration; this is a no-op.
    return null;
};

export const ensureReportContentDoc = async (reportId) => {
    return null;
};

export const getReportReferences = async (reportId) => {
    const data = await apiFetch(`/api/reports/${encodeURIComponent(reportId)}/references`);
    return Array.isArray(data) ? data : [];
};

export const deleteReport = async (reportId) => {
    if (!reportId) throw new Error('reportId is required');
    await apiFetch(`/api/reports/${encodeURIComponent(reportId)}`, {method: 'DELETE'});
    invalidateReportListCache();
};

export const deleteDisclosure = async (reportId, reportData = null) => {
    if (!reportId) throw new Error('reportId is required');
    await apiFetch(`/api/reports/${encodeURIComponent(reportId)}`, {method: 'DELETE'});
    invalidateReportListCache();
};

export const resetReportToDraft = async (reportId) => {
    if (!reportId) throw new Error('reportId is required');
    await apiFetch(`/api/reports/${encodeURIComponent(reportId)}`, {
        method: 'PUT',
        body: JSON.stringify({status: 'Draft', validation: null, authorBlocks: null}),
    });
    invalidateReportListCache();
};

// Re-export useStompSubscription for convenience in components
export {useStompSubscription};
